import React, {
  useState,
  useEffect,
  useContext,
  useRef,
} from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import algoliasearch from 'algoliasearch';
import LocalizationContext from '../../contexts/Localizations';
import Container from '../helpers/Container';
import UnderlineTitle from '../common/UnderlineTitle';
import SvgImage from '../common/SvgImage';
import Loader from '../common/Loader';
import LinkCarousel from '../LinkCarousel';
import NewsCarousel from '../NewsCarousel';
import CompetitionCarousel from '../CompetitionCarousel';
import SearchScreenProps from './type';
import { Image, Link } from '../../typings';
import { Discipline } from '../../core/typings';

import './styles.scss';

interface AlgoliaResult {
  content: string;
  contentType: string;
  createdAt: string;
  newsCategory: string;
  objectID: string;
  tags: string[];
  title: string;
  url: string;
  thumbnailImage?: Image;
  discipline?: Discipline;
  disciplineLink?: string;
  disciplineName?: string;
  articleType?: string;
}

const SearchScreen = ({
  title,
  appId,
  publicKey,
  searchIndex,
  eventsMoreLink,
  newsMoreLink,
}: SearchScreenProps) => {
  const filters: Record<string, string[]> = {
    all: [
      'contentType:page',
      'contentType:news',
      'contentType:event',
      'contentType:resource',
    ],
    page: ['contentType:page'],
    news: ['contentType:news'],
    event: ['contentType:event'],
    resource: ['contentType:resource'],
  };

  const searchClient = algoliasearch(appId, publicKey);
  const index = searchClient.initIndex(searchIndex);
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  const recognition = SpeechRecognition && new SpeechRecognition();

  const { translations } = useContext(LocalizationContext);
  const searchField = useRef<HTMLInputElement | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [refinement, setRefinement] = useState<string>('all');
  const [results, setResults] = useState({});
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [voiceNotAllowed, setVoiceNotAllowed] = useState<boolean>(false);
  const [isSearching, setIsSearching] = useState<boolean>(true);
  const search = (filter?: string) => index.search(searchTerm, {
    facetFilters: filter,
  });

  const handleSearch = async () => {
    setIsSearching(true);
    setResults({});
    const promiseSearches = filters[refinement].map((filter) => search(filter));
    Promise.all(promiseSearches).then((searchResults) => {
      if (refinement === 'all') {
        setResults({
          page: searchResults[0].hits,
          news: searchResults[1].hits,
          event: searchResults[2].hits,
          resource: searchResults[3].hits,
        });
      } else {
        setResults({
          [refinement]: searchResults[0].hits,
        });
      }
      setIsSearching(false);
    });
  };

  const handleClearSearch = () => {
    (searchField?.current as HTMLInputElement)?.focus();
    setSearchTerm('');
  };

  const handleVoiceSearch = () => {
    recognition.onresult = (event: any) => {
      const current = event.resultIndex;

      setIsRecording(false);
      setSearchTerm(event.results[current][0].transcript);
    };

    recognition.onerror = (event: any) => {
      if (event.error === 'not-allowed') {
        setVoiceNotAllowed(true);
      }
    };

    setIsRecording(true);
    recognition.start();
  };

  useEffect(() => {
    handleSearch();
  }, [refinement]);

  useEffect(() => {
    handleSearch();
  }, [searchTerm]);

  const TYPE_LABEL_MAP = {
    all: translations.search.refinementAllLabel,
    page: translations.search.refinementPageLabel,
    event: translations.search.refinementEventLabel,
    news: translations.search.refinementNewsLabel,
    resource: translations.search.refinementResourceLabel,
  } as Record<string, string>;

  const Hits = () => {
    const toCompetition = (item: AlgoliaResult) => ({
      ...item,
      colourCode: item.discipline,
      isDownload: item.contentType === 'resource',
      name: item.title,
      raceHubLink: {
        url: item.url,
        title: item.contentType === 'event'
          ? translations.search.raceHubLinkLabel
          : translations.search.resourceLinkLabel,
      },
      ...(item.contentType === 'event' && {
        disciplineLink: {
          url: item.disciplineLink,
          title: item.disciplineName,
        },
      }),
    });

    const toNews = (item: AlgoliaResult) => ({
      ...item,
      date: item.createdAt,
      type: item.articleType,
      category: {
        title: item.disciplineName,
      },
      thumbnail: item.thumbnailImage,
    });

    const eventSlickConfig = {
      slidesToShow: 4,
      responsive: [
        {
          breakpoint: 1600,
          settings: {
            slidesToShow: 3,
            arrows: true,
          },
        },
        {
          breakpoint: 1000,
          settings: {
            slidesToShow: 2,
            arrows: true,
          },
        },
        {
          breakpoint: 650,
          settings: {
            slidesToShow: 1.5,
            arrows: true,
          },
        },
        {
          breakpoint: 530,
          settings: {
            slidesToShow: 1,
            arrows: true,
          },
        },
      ],
    };

    const newsSlickConfig = {
      slidesToShow: 4,
      responsive: [
        {
          breakpoint: 1600,
          settings: {
            slidesToShow: 3,
            arrows: true,
          },
        },
        {
          breakpoint: 1000,
          settings: {
            slidesToShow: 2,
            arrows: true,
          },
        },
        {
          breakpoint: 650,
          settings: {
            slidesToShow: 1.5,
            arrows: true,
          },
        },
        {
          breakpoint: 530,
          settings: {
            slidesToShow: 1,
            arrows: true,
          },
        },
      ],
    };

    const resourceSlickConfig = {
      slidesToShow: 5,
      responsive: [
        {
          breakpoint: 1600,
          settings: {
            slidesToShow: 4,
            arrows: true,
          },
        },
        {
          breakpoint: 1000,
          settings: {
            slidesToShow: 3,
            arrows: true,
          },
        },
        {
          breakpoint: 650,
          settings: {
            slidesToShow: 2,
            arrows: true,
          },
        },
      ],
    };

    const filteredResults = Object.entries(results).filter(([, items]) => (
      (items as any)?.length > 0));

    return (
      <>
        {isSearching && (
          <div className="search-hits__loader">
            <Loader />
          </div>
        )}
        {Boolean(filteredResults.length === 0 && !isSearching) && (
          <section className="search-hits__no-results">
            <h3 className="search-hits__no-results-title">{translations.search.noResultsTitle}</h3>
            <p className="search-hits__no-results-text">
              {translations.search.noResultsTextPrefix}
              {` ${searchTerm}, `}
              {translations.search.noResultsTextSuffix}
            </p>
          </section>
        )}
        {filteredResults.map(([type, items]) => (
          <section className="search-hits" key={type} aria-label={TYPE_LABEL_MAP[type]}>
            <UnderlineTitle title={TYPE_LABEL_MAP[type]} containerTheme="white" />

            {type === 'page' && (
              <LinkCarousel links={items as Link[]} />
            )}

            {type === 'event' && (
              <CompetitionCarousel
                competitions={(items as any).map(toCompetition)}
                slickConfig={eventSlickConfig}
                viewMoreLink={eventsMoreLink as Link}
                standalone
              />
            )}

            {type === 'news' && (
              <NewsCarousel
                news={(items as any).map(toNews)}
                slickConfig={newsSlickConfig}
                viewMoreLink={newsMoreLink}
              />
            )}

            {type === 'resource' && (
              <CompetitionCarousel
                competitions={(items as any).map(toCompetition)}
                slickConfig={resourceSlickConfig}
                standalone
              />
            )}
          </section>
        ))}
      </>
    );
  };

  return (
    <div className="search-screen">
      <h3 className="sr-only">{title}</h3>

      <section className="search-screen__header" aria-label={title}>
        <PerfectScrollbar className="refinement__list-container">
          <ul className="refinement__list">
            {Object.keys(filters).map((type) => (
              <li key={type} className="refinement__item">
                <a
                  className={`refinement__link ${type === refinement ? 'refinement__link--selected' : ''}`}
                  href="#"
                  onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
                    event.preventDefault();
                    setRefinement(type);
                  }}
                >
                  {TYPE_LABEL_MAP[type]}
                </a>
              </li>
            ))}
          </ul>
        </PerfectScrollbar>

        <Container>
          <form className="search-box" action="">
            <label className="sr-only" htmlFor="search-box-input">{translations.search.searchLabel}</label>
            <input
              ref={searchField}
              id="search-box-input"
              className="search-box__input"
              type="text"
              placeholder={translations.search.searchLabel}

            />
            {searchTerm && (
              <button
                className="search-box__clear-trigger"
                type="button"
                onClick={() => handleClearSearch}
                aria-label={translations.search.clearSearchLabel}
              />
            )}
            {recognition && (
              <button
                className={`search-box__voice-trigger ${(isRecording && !voiceNotAllowed) ? 'search-box__voice-trigger--on' : ''} ${voiceNotAllowed ? 'search-box__voice-trigger--disabled' : ''}`}
                type="button"
                onClick={handleVoiceSearch}
                aria-pressed={isRecording}
                aria-label={translations.search.voiceSearchLabel}
                disabled={voiceNotAllowed}
              >
                <SvgImage
                  title=""
                  reference={voiceNotAllowed ? '#icon-microphone-blocked' : '#icon-microphone'}
                  ariaHidden
                />
              </button>
            )}
            <button
              className="search-box__submit"
              type="submit"
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                event.preventDefault();
                setSearchTerm(searchField?.current ? (searchField?.current as HTMLInputElement)?.value : '');
              }}
              aria-label={translations.search.searchSubmitLabel}
            >
              <SvgImage title="" reference="#icon-think-arrow" ariaHidden />
            </button>
          </form>
        </Container>
      </section>

      <div className="search-screen__results">
        <Container>
          <div className="search-screen__results-content">
            <Hits />
          </div>
        </Container>
      </div>
    </div>
  );
};

export default SearchScreen;
